// Build the flat row list that the admin pricing matrix page renders.
// One pass over the catalog: categories in display order, products in board
// order, options in display order. Simple products (no options) become a
// single priceable row keyed by product_id; option-bearing products become a
// non-editable product header sub-row followed by one priceable row per
// option, each keyed by product_option_id.
//
// Each priceable row carries its current tiers (canonicalized to the 4
// buckets) so the table can render savedRate without re-querying the slice.
import { tiersFromRule } from '../config/pricing-buckets.js';
import { strategyFromRule, amountFromRule } from '../config/pricing-strategies.js';
import {
  findProductRule, findOptionRule, optionsForProduct,
} from './catalog-helpers.js';

const sortByOrderThenName = (a, b) => (
  (a.display_order - b.display_order) || a.name.localeCompare(b.name)
);

// rowKey is stable across renders and unique per priceable target. We prefix
// to keep product rows from colliding with option rows that share an id space.
export const productRowKey = (productId) => `product:${productId}`;
export const optionRowKey = (optionId) => `option:${optionId}`;

// Build the flat list. Header rows (kind: 'product-header') are interleaved
// for option-bearing products and have no `target`/`tiers`; the table renders
// them as a non-editable label band above their options.
//
// Pass `strategy` to restrict the list to targets priced with that strategy
// (used to split the matrix into a section per strategy). When omitted, every
// target is included regardless of strategy.
export const buildPricingRows = ({
  categories = [], products = [], options = [], pricing = [], strategy = null,
} = {}) => {
  const rows = [];
  const sortedCategories = [...categories].sort(sortByOrderThenName);
  const matchesStrategy = (rowStrategy) => strategy == null || rowStrategy === strategy;

  sortedCategories.forEach((category) => {
    const categoryProducts = products
      .filter((p) => p.category_id === category.id)
      .sort(sortByOrderThenName);

    categoryProducts.forEach((product) => {
      const productOptions = optionsForProduct(options, product.id);
      const shared = {
        categoryId: category.id,
        categoryName: category.name,
        productId: product.id,
        productName: product.name,
        productIsActive: product.is_active,
        selectionMode: product.selection_mode,
        iconKey: product.icon_key,
      };

      if (productOptions.length === 0) {
        const rule = findProductRule(pricing, product.id);
        const rowStrategy = strategyFromRule(rule);
        if (!matchesStrategy(rowStrategy)) return;
        rows.push({
          ...shared,
          key: productRowKey(product.id),
          kind: 'product',
          isActive: product.is_active,
          target: { product_id: product.id },
          strategy: rowStrategy,
          tiers: tiersFromRule(rule?.payload?.tiers ?? null),
          amount: amountFromRule(rule),
        });
        return;
      }

      // Resolve each option's strategy first so we can skip the header entirely
      // when no option matches the requested strategy.
      const optionRows = productOptions
        .map((option) => {
          const rule = findOptionRule(pricing, option.id);
          const rowStrategy = strategyFromRule(rule);
          if (!matchesStrategy(rowStrategy)) return null;
          return {
            ...shared,
            key: optionRowKey(option.id),
            kind: 'option',
            optionId: option.id,
            optionName: option.name,
            isActive: product.is_active && option.is_active,
            target: { product_option_id: option.id },
            strategy: rowStrategy,
            tiers: tiersFromRule(rule?.payload?.tiers ?? null),
            amount: amountFromRule(rule),
          };
        })
        .filter(Boolean);

      if (optionRows.length === 0) return;

      rows.push({
        ...shared,
        key: `product-header:${product.id}`,
        kind: 'product-header',
        isActive: product.is_active,
      });
      optionRows.forEach((row) => rows.push(row));
    });
  });

  return rows;
};

// Filter a row list by a free-text query and a set of selected category ids.
// Keeps a product-header row only when at least one of its option rows passes;
// otherwise the header would orphan. Empty selectedCategoryIds means "all".
export const filterPricingRows = (rows, { search = '', selectedCategoryIds = [] } = {}) => {
  const needle = search.trim().toLowerCase();
  const categorySet = selectedCategoryIds.length ? new Set(selectedCategoryIds) : null;

  const matchesSearch = (row) => {
    if (!needle) return true;
    const haystack = [row.categoryName, row.productName, row.optionName]
      .filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(needle);
  };
  const matchesCategory = (row) => !categorySet || categorySet.has(row.categoryId);

  const passes = rows.map((row) => (
    row.kind === 'product-header' ? false : matchesSearch(row) && matchesCategory(row)
  ));

  // Promote header rows when any of their child option rows pass.
  rows.forEach((row, i) => {
    if (row.kind !== 'product-header') return;
    if (!matchesCategory(row)) return;
    for (let j = i + 1; j < rows.length; j += 1) {
      const next = rows[j];
      if (next.kind !== 'option' || next.productId !== row.productId) break;
      if (passes[j]) { passes[i] = true; break; }
    }
    // A header also passes if the search itself matches the product/category.
    if (!passes[i] && matchesSearch(row)) {
      // ...but only if at least one of its options is in the filtered set,
      // otherwise the header would render with no children below it.
      for (let j = i + 1; j < rows.length; j += 1) {
        const next = rows[j];
        if (next.kind !== 'option' || next.productId !== row.productId) break;
        if (matchesCategory(next)) { passes[i] = true; passes[j] = true; }
      }
    }
  });

  return rows.filter((_, i) => passes[i]);
};
